import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'

interface ActionItem {
  label: string
  icon?: string
  onClick: () => void
  className?: string
  disabled?: boolean
}

interface ActionMenuProps {
  actions: ActionItem[]
  className?: string
}

const ActionMenu: React.FC<ActionMenuProps> = ({ actions, className }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 })
  const menuRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  // Calculate dropdown position and close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    const calculatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect()
        setDropdownPosition({
          top: rect.bottom + 4, // Position below the button
          left: rect.left, // Align with left edge of button
        })
      }
    }

    if (isOpen) {
      calculatePosition()
    }

    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('resize', calculatePosition)
    window.addEventListener('scroll', calculatePosition)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('resize', calculatePosition)
      window.removeEventListener('scroll', calculatePosition)
    }
  }, [isOpen])

  // Show the primary action directly as a button, with additional actions in dropdown
  const primaryAction = actions[0]
  const hasMoreActions = actions.length > 1

  return (
    <div className={css(styles.menuContainer)} ref={menuRef}>
      {/* Primary action shown directly as a button */}
      {primaryAction && (
        <button
          className={css(styles.primaryActionButton)}
          onClick={() => primaryAction.onClick()}
          disabled={primaryAction.disabled}
        >
          {primaryAction.icon && <i className={primaryAction.icon} />}
          {primaryAction.label}
        </button>
      )}

      {/* Show dropdown toggle only if there are additional actions */}
      {hasMoreActions && (
        <>
          <button
            ref={buttonRef}
            className={css(styles.moreActionsButton)}
            onClick={() => setIsOpen(!isOpen)}
            aria-label="More actions"
          >
            <i className="fa-solid fa-chevron-down" />
          </button>

          {isOpen && (
            <div
              className={css(styles.menuDropdown)}
              style={{
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
              }}
            >
              {actions.slice(1).map((action, index) => (
                <button
                  key={`action-${index}-${action.label}`}
                  className={css(styles.menuItem)}
                  onClick={() => {
                    action.onClick()
                    setIsOpen(false)
                  }}
                  disabled={action.disabled}
                >
                  {action.icon && <i className={action.icon} />}
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

const styles = StyleSheet.create({
  menuContainer: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  primaryActionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    backgroundColor: '#1C64F2',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    transition: 'background-color 0.2s ease',
    ':hover': {
      backgroundColor: '#1955CC',
    },
    ':disabled': {
      backgroundColor: '#9CA3AF',
      cursor: 'not-allowed',
    },
  },
  moreActionsButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    backgroundColor: '#F3F4F6',
    border: '1px solid #E5E7EB',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#6B7280',
    fontSize: '10px',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#E5E7EB',
      color: '#374151',
    },
    ':focus': {
      outline: 'none',
      boxShadow: '0 0 0 2px #3B82F6',
    },
  },
  hamburgerButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    backgroundColor: '#F3F4F6',
    border: '1px solid #E5E7EB',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#6B7280',
    fontSize: '14px',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#E5E7EB',
      color: '#374151',
    },
    ':focus': {
      outline: 'none',
      boxShadow: '0 0 0 2px #3B82F6',
    },
  },
  menuDropdown: {
    position: 'fixed',
    backgroundColor: 'white',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    boxShadow:
      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    zIndex: 9999,
    minWidth: '160px',
    overflow: 'hidden',
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    padding: '8px 12px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#374151',
    textAlign: 'left',
    transition: 'background-color 0.2s ease',
    ':hover': {
      backgroundColor: '#F3F4F6',
    },
    ':disabled': {
      color: '#9CA3AF',
      cursor: 'not-allowed',
    },
  },
  singleActionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: '#1C64F2',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 500,
    transition: 'background-color 0.2s ease',
    ':hover': {
      backgroundColor: '#1955CC',
    },
    ':disabled': {
      backgroundColor: '#9CA3AF',
      cursor: 'not-allowed',
      ':hover': {
        backgroundColor: '#9CA3AF',
      },
    },
  },
})

export default ActionMenu
